import { useEffect, useRef } from 'react';
import type L from 'leaflet';
import type { ClusterFeature, PointFeature } from 'supercluster';
import { Post, Structure, MapClaimSummary, normalizePostCategory } from '../../types';
import type { PostCategory } from '../../types';
import { useTheme, ON_COLOR, CATEGORY_GLYPH_PATHS, TRIANGLE_PATH } from '../../theme';
import type { StructureBadge } from '../../utils/structureBadges';
import { getStructureMarkerColor } from './markerColor';
import { joinClaim, useStructuresStore } from '../../store/structuresStore';
import { useUIStore } from '../../store/uiStore';
import {
  getLeaflet,
  makePointIcon,
  makeClusterIcon,
  makeObservationClusterIcon,
  setPostMarkerDocked,
  type PointIconBadge,
} from './leafletHelpers';
import { MAX_ZOOM, STRUCTURE_RENDER_MIN_ZOOM_WEB } from './types';
import { ClusterInstance, StructurePointProps } from './useClusterIndex.web';
import { dominantCategory, type CategoryTally } from './clusterTint';
import Supercluster from 'supercluster';
import {
  POST_DOT_SIZE,
  POST_DOT_BORDER,
  POST_DOT_HALO,
  POST_DELTA_SIZE,
  POST_GLYPH_SIZE,
  STRUCTURE_PIN_SIZE,
  STRUCTURE_PIN_SIZE_SELECTED,
  STRUCTURE_PIN_BORDER,
  STRUCTURE_PIN_BORDER_SELECTED,
  Z_POST,
  Z_SELECTED,
  Z_STRUCTURE,
} from './markerSizes';

type PostPointProps = {
  postId: string;
  // Per-category counters. A leaf carries a 1 in its own column; Supercluster's
  // map/reduce sums them up the tree so a cluster knows its own mix without a
  // second pass over the posts.
  progress: number;
  risk: number;
  news: number;
};

// Translate the primitive badge value into the icon-shape param
// makePointIcon expects, resolving category colors via the live theme
// (colorsRef, same convention as claim colors below).
function toPointIconBadge(
  badge: StructureBadge | undefined,
  colors: ReturnType<typeof useTheme>['colors']
): PointIconBadge | undefined {
  if (badge === undefined) return undefined;
  if (badge === 'delta') return { kind: 'delta', color: colors.mapMarkerDelta };
  return {
    kind: 'dot',
    color: badge === 'risk' ? colors.mapMarkerOpportunity : colors.mapMarkerProgress,
  };
}

interface UseMarkerLayerParams {
  leafletMapRef: React.MutableRefObject<L.Map | null>;
  layerRef: React.MutableRefObject<L.LayerGroup | null>;
  markersRef: React.MutableRefObject<Map<string, L.Marker>>;
  postMarkersRef: React.MutableRefObject<Map<string, L.Marker>>;
  // Keyed by post id -> `${fill}|${category}|${hasDelta}`, the composite of
  // everything that changes which icon a post renders — not just fill color
  // (category picks the glyph, hasDelta toggles the before/after badge).
  postMarkerIconKeyRef: React.MutableRefObject<Map<string, string>>;
  superclusterRef: React.MutableRefObject<ClusterInstance | null>;
  renderClustersRef: React.MutableRefObject<() => void>;
  restylePointsRef: React.MutableRefObject<(ids: Iterable<string>) => void>;
  claimsRef: React.MutableRefObject<Record<string, MapClaimSummary>>;
  // Live structureId -> badge map (postsStore.structurePostBadges), read via
  // ref for the same reason claimsRef is — render()/restylePoints() run
  // imperatively inside Leaflet event handlers and need the current value
  // without re-registering those handlers.
  badgesRef: React.MutableRefObject<Record<string, StructureBadge>>;
  selectedIdRef: React.MutableRefObject<string | null>;
  colorsRef: React.MutableRefObject<ReturnType<typeof useTheme>['colors']>;
  onStructurePressRef: React.MutableRefObject<((structure: Structure) => void) | undefined>;
  isLoaded: boolean;
  posts: Post[];
  claimsByStructureId: Record<string, MapClaimSummary>;
  structurePostBadges: Record<string, StructureBadge>;
  selectedStructure: Structure | null;
  colors: ReturnType<typeof useTheme>['colors'];
  onPostPress?: (post: Post) => void;
  onPostHover?: (post: Post, point: { x: number; y: number }) => void;
  onPostHoverEnd?: () => void;
  // Observations layer toggle (uiStore.useObservationsVisible) — structure
  // pins/clusters are untouched by this; only post markers and observation
  // clusters are added/removed. False leaves the Supercluster index and the
  // `posts` subscription alone so switching back on repaints instantly.
  visible: boolean;
}

/**
 * Owns the imperative marker layer for the web Leaflet map.
 *
 * Populates renderClustersRef.current (full diff-based cluster+point render)
 * and restylePointsRef.current (surgical per-id restyle) once isLoaded is
 * true. Also owns the two reactive effects: claim/selection restyle and
 * post marker sync.
 */
export function useMarkerLayer({
  leafletMapRef,
  layerRef,
  markersRef,
  postMarkersRef,
  postMarkerIconKeyRef,
  superclusterRef,
  renderClustersRef,
  restylePointsRef,
  claimsRef,
  badgesRef,
  selectedIdRef,
  colorsRef,
  onStructurePressRef,
  isLoaded,
  posts,
  claimsByStructureId,
  structurePostBadges,
  selectedStructure,
  colors,
  onPostPress,
  onPostHover,
  onPostHoverEnd,
  visible,
}: UseMarkerLayerParams): void {
  // Read at call time by renderPosts, same convention as the callback refs
  // below — assigned during render (not an effect) so it's already current
  // the first time renderPosts runs.
  const visibleRef = useRef(visible);
  visibleRef.current = visible;

  // Observation clustering. Posts used to be placed once, straight onto the
  // map, and left there: the effect below only re-ran when `posts` changed, so
  // nothing ever merged them. At Big Bear density that meant ~860 dots on
  // screen at once, a quarter of them drawn exactly behind another one. They
  // now go through their own Supercluster index and are re-rendered on pan and
  // zoom alongside the structure pins.
  // The post open in the right dock: its dot wears the selected ring. Read
  // by renderPosts (a dot built or re-iconed mid-pan must pick it up) and by
  // the effect below, which restyles only the old and new dots.
  const dockedPostId = useUIStore((s) => s.dockedPostId);
  const dockedPostIdRef = useRef(dockedPostId);
  dockedPostIdRef.current = dockedPostId;
  const prevDockedPostIdRef = useRef<string | null>(null);
  const postClusterRef = useRef<Supercluster<PostPointProps, CategoryTally> | null>(null);
  const postsByIdRef = useRef<Map<string, Post>>(new Map());
  const renderPostsRef = useRef<() => void>(() => {});

  // The observation render is wired once and reads its callbacks at call
  // time, the same convention onStructurePressRef follows. Assigned during
  // render, not in an effect, so they are already current the first time the
  // wiring effect below runs.
  const onPostPressRef = useRef(onPostPress);
  const onPostHoverRef = useRef(onPostHover);
  const onPostHoverEndRef = useRef(onPostHoverEnd);
  onPostPressRef.current = onPostPress;
  onPostHoverRef.current = onPostHover;
  onPostHoverEndRef.current = onPostHoverEnd;

  // Snapshot refs for the claim/selection diff effect.
  const prevClaimsRef = useRef<
    Record<string, { claimedBy: string; completed: number; total: number }>
  >({});
  const prevSelectedIdRef = useRef<string | null>(null);
  // Snapshot ref for the badge diff effect below.
  const prevBadgesRef = useRef<Record<string, StructureBadge>>({});

  // Wire up renderClustersRef and restylePointsRef once the map is ready.
  // These functions read everything from refs at call time so they are safe
  // to define once and remain stable across reactive re-renders.
  useEffect(() => {
    if (!isLoaded) return;

    // Imperative diff-based render. Called on every pan/zoom and on
    // cluster-index / claim / selection changes. Keys identify features
    // so markers for features that stay visible across a pan are reused
    // instead of torn down — that's what keeps pins from visually
    // jumping when you drag the map.
    const render = () => {
      const layerNow = layerRef.current;
      const sc = superclusterRef.current;
      const mapNow = leafletMapRef.current;
      // Observations do not depend on the structure index, which is built
      // lazily — render them even on the early passes where it is still null,
      // or the first paint of the map has no dots on it.
      if (!layerNow || !sc || !mapNow) {
        renderPostsRef.current();
        return;
      }

      const bounds = mapNow.getBounds();
      const zoom = Math.floor(mapNow.getZoom());
      // Observations-first: below neighborhood zoom render no structure
      // pins or cluster bubbles at all — the wide view leads with
      // observation dots. An empty feature list makes the removal loop
      // below tear down any structure markers left from a closer zoom.
      // Shard data still loads at wide zoom (address search needs it).
      const structuresVisible = zoom >= STRUCTURE_RENDER_MIN_ZOOM_WEB;
      // Tell the map key which side of that gate we're on, so it can offer
      // "Zoom in for properties" while they're hidden.
      useUIStore.getState().setPropertyPinsVisible(structuresVisible);
      const features = structuresVisible
        ? sc.getClusters(
            [
              Math.max(-180, bounds.getWest()),
              Math.max(-85, bounds.getSouth()),
              Math.min(180, bounds.getEast()),
              Math.min(85, bounds.getNorth()),
            ],
            zoom
          )
        : [];

      const claims = claimsRef.current;
      const badges = badgesRef.current;
      const selectedId = selectedIdRef.current;
      const currentColors = colorsRef.current;

      // Position-bucket precision: round to ~half-tile at the current
      // zoom. Cluster markers land in the same bucket across index
      // rebuilds whenever supercluster re-places them at the same spot,
      // so the marker is reused instead of torn down.
      const bucketScale = 2 ** Math.max(0, zoom + 1);

      const nextKeys = new Set<string>();
      for (const f of features) {
        const [lng, lat] = f.geometry.coordinates;
        if ((f.properties as { cluster?: boolean }).cluster) {
          const cf = f as ClusterFeature<StructurePointProps>;
          const latBucket = Math.round(lat * bucketScale);
          const lngBucket = Math.round(lng * bucketScale);
          const count = cf.properties.point_count;
          const countBucket = Math.floor(Math.log10(Math.max(1, count)));
          const key = `c:${zoom}:${latBucket}:${lngBucket}:${countBucket}`;
          nextKeys.add(key);
          const existingCluster = markersRef.current.get(key);
          if (existingCluster) {
            // Count in the same log-bucket can still vary; refresh the
            // icon so the badge reflects current count.
            existingCluster.setIcon(makeClusterIcon(count, cf.properties.point_count_abbreviated));
            // Rebind click handler so the cluster_id captured in the
            // closure matches the current index generation.
            existingCluster.off('click');
            existingCluster.on('click', () => {
              try {
                const expansionZoom = sc.getClusterExpansionZoom(cf.properties.cluster_id);
                mapNow.setView([lat, lng], Math.min(expansionZoom, MAX_ZOOM));
              } catch {
                mapNow.setView([lat, lng], Math.min(zoom + 2, MAX_ZOOM));
              }
            });
            continue;
          }

          const marker = getLeaflet().marker([lat, lng], {
            icon: makeClusterIcon(
              cf.properties.point_count,
              cf.properties.point_count_abbreviated,
              { animate: true }
            ),
          });
          marker.on('click', () => {
            try {
              const expansionZoom = sc.getClusterExpansionZoom(cf.properties.cluster_id);
              mapNow.setView([lat, lng], Math.min(expansionZoom, 18));
            } catch {
              mapNow.setView([lat, lng], Math.min(zoom + 2, 18));
            }
          });
          marker.addTo(layerNow);
          markersRef.current.set(key, marker);
        } else {
          const props = (f as PointFeature<StructurePointProps>).properties;
          const structureId = props.structureId;
          const key = `p:${structureId}`;
          nextKeys.add(key);

          const isSelected = selectedId === structureId;
          const claim = claims[structureId];
          const snap = claim?.progressSnapshot;
          const progressPercent =
            snap && snap.total > 0 ? Math.round((snap.completed / snap.total) * 100) : undefined;
          const pseudo: Structure = {
            id: structureId,
            centroid: { latitude: lat, longitude: lng },
            address: props.address,
            city: '',
            state: '',
            postsCount: 0,
            createdAt: null as unknown as Structure['createdAt'],
            updatedAt: null as unknown as Structure['updatedAt'],
            claimSummary: claim ? { claimedBy: claim.claimedBy, progressPercent } : undefined,
          };
          const color = getStructureMarkerColor(pseudo, isSelected, currentColors);
          const size = isSelected ? STRUCTURE_PIN_SIZE_SELECTED : STRUCTURE_PIN_SIZE;
          const border = isSelected ? STRUCTURE_PIN_BORDER_SELECTED : STRUCTURE_PIN_BORDER;
          const pointBadge = toPointIconBadge(badges[structureId], currentColors);

          const existing = markersRef.current.get(key);
          if (existing) {
            // Reuse: only restyle if selection / claim might have
            // changed the icon since last render. No `animate` — this icon
            // element already exists on screen, and the entrance keyframe
            // would replay on every pan/zoom rebuild otherwise.
            existing.setIcon(
              makePointIcon(color, size, border, pointBadge, { selected: isSelected })
            );
            existing.setZIndexOffset(isSelected ? Z_SELECTED : Z_STRUCTURE);
            continue;
          }

          const marker = getLeaflet().marker([lat, lng], {
            icon: makePointIcon(color, size, border, pointBadge, {
              animate: true,
              selected: isSelected,
            }),
            zIndexOffset: isSelected ? Z_SELECTED : Z_STRUCTURE,
          });
          if (props.address) {
            const label = props.address.trim().split(/\s+/)[0];
            if (label) {
              marker.bindTooltip(label, {
                direction: 'right',
                offset: [10, 0],
                className: 'structure-label',
              });
            }
          }
          marker.on('click', () => {
            const handler = onStructurePressRef.current;
            if (!handler) return;
            const state = useStructuresStore.getState();
            const byShard = state._structuresByShard;
            for (const sk in byShard) {
              const arr = byShard[sk];
              for (let i = 0; i < arr.length; i++) {
                if (arr[i].id === structureId) {
                  handler(arr[i]);
                  return;
                }
              }
            }
            // User-added structures live in a separate live cache —
            // they're rendered alongside the shard markers but not
            // present in _structuresByShard, so a shard-only lookup
            // would silently drop the click.
            const userAdded = state.userAddedStructures;
            for (let i = 0; i < userAdded.length; i++) {
              if (userAdded[i].id === structureId) {
                handler(userAdded[i]);
                return;
              }
            }
          });
          marker.addTo(layerNow);
          markersRef.current.set(key, marker);
        }
      }

      // Remove markers whose features are no longer in view.
      for (const [key, marker] of markersRef.current) {
        if (!nextKeys.has(key)) {
          marker.off();
          layerNow.removeLayer(marker);
          markersRef.current.delete(key);
        }
      }

      // Observations share the pan/zoom cycle with the structure pins so
      // their clusters re-form as the viewport changes.
      renderPostsRef.current();
    };
    renderClustersRef.current = render;

    // Surgical restyle: rebuild the icon for just the given structure
    // ids if their point markers are currently visible. No-op for
    // clusters or for points not currently drawn. This is the hot
    // path for claim updates — O(|changed|) instead of O(|viewport|).
    const restylePoints = (ids: Iterable<string>) => {
      const claims = claimsRef.current;
      const badges = badgesRef.current;
      const selectedId = selectedIdRef.current;
      const currentColors = colorsRef.current;
      const state = useStructuresStore.getState();
      const byShard = state._structuresByShard;
      const userAdded = state.userAddedStructures;
      for (const id of ids) {
        const marker = markersRef.current.get(`p:${id}`);
        if (!marker) continue;
        let structure: Structure | null = null;
        for (const sk in byShard) {
          const arr = byShard[sk];
          for (let i = 0; i < arr.length; i++) {
            if (arr[i].id === id) {
              structure = arr[i];
              break;
            }
          }
          if (structure) break;
        }
        if (!structure) {
          for (let i = 0; i < userAdded.length; i++) {
            if (userAdded[i].id === id) {
              structure = userAdded[i];
              break;
            }
          }
        }
        if (!structure) continue;
        const claim = claims[id];
        const pseudo: Structure = joinClaim(structure, claim);
        const isSelected = selectedId === id;
        const color = getStructureMarkerColor(pseudo, isSelected, currentColors);
        const size = isSelected ? STRUCTURE_PIN_SIZE_SELECTED : STRUCTURE_PIN_SIZE;
        const border = isSelected ? STRUCTURE_PIN_BORDER_SELECTED : STRUCTURE_PIN_BORDER;
        marker.setIcon(
          makePointIcon(color, size, border, toPointIconBadge(badges[id], currentColors), {
            selected: isSelected,
          })
        );
        marker.setZIndexOffset(isSelected ? Z_SELECTED : Z_STRUCTURE);
      }
    };
    restylePointsRef.current = restylePoints;
    // Populate refs once on mount (isLoaded gate). The functions themselves
    // read all live data from refs, so they don't need to re-run on updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded]);

  // Claim updates / selection changes — restyle only the affected point
  // markers. Clusters are untouched because claims don't affect the index.
  useEffect(() => {
    if (!isLoaded) return;
    const prevClaims = prevClaimsRef.current;
    const nextClaims = claimsByStructureId;
    const changed = new Set<string>();
    for (const id in nextClaims) {
      const prev = prevClaims[id];
      const next = nextClaims[id];
      const nextSnap = next.progressSnapshot;
      const nextCompleted = nextSnap?.completed ?? 0;
      const nextTotal = nextSnap?.total ?? 0;
      if (
        !prev ||
        prev.claimedBy !== next.claimedBy ||
        prev.completed !== nextCompleted ||
        prev.total !== nextTotal
      ) {
        changed.add(id);
      }
    }
    for (const id in prevClaims) {
      if (!(id in nextClaims)) changed.add(id);
    }
    const nextSelectedId = selectedStructure?.id ?? null;
    const prevSelectedId = prevSelectedIdRef.current;
    if (nextSelectedId !== prevSelectedId) {
      if (prevSelectedId) changed.add(prevSelectedId);
      if (nextSelectedId) changed.add(nextSelectedId);
    }
    if (changed.size > 0) {
      restylePointsRef.current(changed);
    }
    // Shallow-copy just the fields we diff on so the next comparison is O(1)
    // per claim and doesn't hold onto full claim objects.
    const slim: Record<string, { claimedBy: string; completed: number; total: number }> = {};
    for (const id in nextClaims) {
      const c = nextClaims[id];
      slim[id] = {
        claimedBy: c.claimedBy,
        completed: c.progressSnapshot?.completed ?? 0,
        total: c.progressSnapshot?.total ?? 0,
      };
    }
    prevClaimsRef.current = slim;
    prevSelectedIdRef.current = nextSelectedId;
    // restylePointsRef is a stable ref — its identity never changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimsByStructureId, selectedStructure, isLoaded]);

  // Structure-marker post badge changes — restyle only the affected point
  // markers. Mirrors the claim/selection diff effect above; badges don't
  // affect the cluster index so clusters are untouched.
  useEffect(() => {
    if (!isLoaded) return;
    const prevBadges = prevBadgesRef.current;
    const nextBadges = structurePostBadges;
    const changed = new Set<string>();
    for (const id in nextBadges) {
      if (prevBadges[id] !== nextBadges[id]) changed.add(id);
    }
    for (const id in prevBadges) {
      if (!(id in nextBadges)) changed.add(id);
    }
    if (changed.size > 0) {
      restylePointsRef.current(changed);
    }
    prevBadgesRef.current = nextBadges;
    // restylePointsRef is a stable ref — its identity never changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [structurePostBadges, isLoaded]);

  // --- Observation markers, clustered ---
  //
  // Two effects: this one owns the icon builders and the imperative render,
  // and the one below rebuilds the index when `posts` changes. The render is
  // driven by renderPostsRef, called from render() on every pan and zoom.
  useEffect(() => {
    if (!isLoaded) return;

    // Observations are the content the map leads with. A centered glyph
    // (check/exclamation) carries category at a glance. Posts with a
    // before/after pair render as a solid delta triangle instead of the
    // colored dot — the whole-pin shape change (not a corner badge) is what
    // flags a before/after post, per client direction.
    // Glyph/triangle paths come from the shared
    // `CATEGORY_GLYPH_PATHS`/`TRIANGLE_PATH` theme tokens (kept in sync with
    // the native pins and the create-post selector) since this is an
    // imperative Leaflet divIcon (raw HTML), not a React tree that could
    // render <CategoryPinGlyph/> directly.
    const buildPostIcon = (
      fill: string,
      category: PostCategory,
      hasDelta: boolean,
      animate = false
    ) => {
      const L = getLeaflet();
      const pinClass = animate ? 'redi-pin redi-pin--enter' : 'redi-pin';
      if (hasDelta) {
        const stroke = (POST_DOT_BORDER * 24) / POST_DELTA_SIZE;
        const svg = `<svg width="${POST_DELTA_SIZE}" height="${POST_DELTA_SIZE}" viewBox="0 0 24 24" fill="${colorsRef.current.mapMarkerDelta}" stroke="${ON_COLOR.fill}" stroke-width="${stroke}" stroke-linejoin="round" style="filter:drop-shadow(0 2px 6px rgba(0,0,0,0.35));"><path d="${TRIANGLE_PATH}"/></svg>`;
        return L.divIcon({
          className: 'post-marker post-marker-delta',
          html: `<div class="${pinClass}" style="display:block;width:${POST_DELTA_SIZE}px;height:${POST_DELTA_SIZE}px;">${svg}</div>`,
          iconSize: [POST_DELTA_SIZE, POST_DELTA_SIZE],
          iconAnchor: [POST_DELTA_SIZE / 2, POST_DELTA_SIZE / 2],
        });
      }
      const glyphPaths = CATEGORY_GLYPH_PATHS[category].map((d) => `<path d="${d}"/>`).join('');
      const glyph = `<svg width="${POST_GLYPH_SIZE}" height="${POST_GLYPH_SIZE}" viewBox="0 0 24 24" fill="none" stroke="${ON_COLOR.fill}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);">${glyphPaths}</svg>`;
      return L.divIcon({
        className: 'post-marker',
        html: `<div class="${pinClass}" style="position:relative;overflow:visible;width:${POST_DOT_SIZE}px;height:${POST_DOT_SIZE}px;background:${fill};border:${POST_DOT_BORDER}px solid white;border-radius:50%;box-shadow:0 0 0 ${POST_DOT_HALO}px ${fill}40, 0 2px 6px rgba(0,0,0,0.35);">${glyph}</div>`,
        iconSize: [POST_DOT_SIZE, POST_DOT_SIZE],
        iconAnchor: [POST_DOT_SIZE / 2, POST_DOT_SIZE / 2],
      });
    };

    const postFill = (category: PostCategory) => {
      const colorsNow = colorsRef.current;
      return category === 'risk'
        ? colorsNow.mapMarkerOpportunity
        : category === 'news'
          ? colorsNow.mapMarkerNews
          : colorsNow.mapMarkerProgress;
    };

    const clearPostMarkers = (mapNow: L.Map) => {
      for (const [key, marker] of postMarkersRef.current) {
        marker.off();
        mapNow.removeLayer(marker);
        postMarkersRef.current.delete(key);
        postMarkerIconKeyRef.current.delete(key);
      }
    };

    const renderPosts = () => {
      const mapNow = leafletMapRef.current;
      const index = postClusterRef.current;
      if (!mapNow || !index) return;
      // Layer switched off (uiStore observations toggle, e.g. while a
      // defense plan is open) — tear the markers off the map but leave the
      // Supercluster index and the `posts` subscription alone, so flipping
      // the toggle back repaints instantly. Removing (not just hiding) the
      // markers is also what stops the hover card and click-to-open from
      // firing: there's no marker left to dispatch the DOM event.
      if (!visibleRef.current) {
        clearPostMarkers(mapNow);
        return;
      }
      const L = getLeaflet();
      const bounds = mapNow.getBounds();
      const zoom = Math.floor(mapNow.getZoom());

      const features = index.getClusters(
        [
          Math.max(-180, bounds.getWest()),
          Math.max(-85, bounds.getSouth()),
          Math.min(180, bounds.getEast()),
          Math.min(85, bounds.getNorth()),
        ],
        zoom
      );

      // Same position-bucket reuse trick as the structure clusters: a bubble
      // that lands in the same spot across an index rebuild is restyled
      // rather than torn down, so it doesn't flicker on pan.
      const bucketScale = 2 ** Math.max(0, zoom + 1);
      const nextKeys = new Set<string>();

      for (const f of features) {
        const [lng, lat] = f.geometry.coordinates;

        if ((f.properties as { cluster?: boolean }).cluster) {
          const cf = f as ClusterFeature<PostPointProps & CategoryTally>;
          const count = cf.properties.point_count;
          const lead = dominantCategory(cf.properties);
          const tint = lead ? postFill(lead) : undefined;
          const countBucket = Math.floor(Math.log10(Math.max(1, count)));
          const key = `oc:${zoom}:${Math.round(lat * bucketScale)}:${Math.round(
            lng * bucketScale
          )}:${countBucket}:${lead ?? 'mixed'}`;
          nextKeys.add(key);

          const zoomToCluster = () => {
            try {
              const expansionZoom = index.getClusterExpansionZoom(cf.properties.cluster_id);
              mapNow.setView([lat, lng], Math.min(expansionZoom, MAX_ZOOM));
            } catch {
              mapNow.setView([lat, lng], Math.min(zoom + 2, MAX_ZOOM));
            }
          };

          const existingCluster = postMarkersRef.current.get(key);
          if (existingCluster) {
            existingCluster.setIcon(makeObservationClusterIcon(count, tint));
            // Rebind so the cluster_id in the closure matches this
            // generation of the index.
            existingCluster.off('click');
            existingCluster.on('click', zoomToCluster);
            continue;
          }

          const clusterMarker = L.marker([lat, lng], {
            icon: makeObservationClusterIcon(count, tint, { animate: true }),
            zIndexOffset: Z_POST,
          });
          clusterMarker.on('click', zoomToCluster);
          clusterMarker.addTo(mapNow);
          postMarkersRef.current.set(key, clusterMarker);
          continue;
        }

        const postId = (f.properties as PostPointProps).postId;
        const post = postsByIdRef.current.get(postId);
        if (!post) continue;
        const key = `o:${postId}`;
        nextKeys.add(key);

        const category = normalizePostCategory(post.category);
        const fill = postFill(category);
        const hasDelta = !!(post.beforeImageUrl || post.beforePostId);
        const iconKey = `${fill}|${category}|${hasDelta}`;

        const existing = postMarkersRef.current.get(key);
        if (existing) {
          // If the post's category/fill/before-after status was edited the
          // rendered icon drifts from the current value — refresh it so the
          // dot matches.
          if (postMarkerIconKeyRef.current.get(key) !== iconKey) {
            existing.setIcon(buildPostIcon(fill, category, hasDelta));
            postMarkerIconKeyRef.current.set(key, iconKey);
            setPostMarkerDocked(existing, postId === dockedPostIdRef.current);
          }
          continue;
        }

        const marker = L.marker([lat, lng], {
          icon: buildPostIcon(fill, category, hasDelta, true),
          // Keep observation dots above structure pins (selected structures
          // use Z_SELECTED; structure clusters use Z_STRUCTURE). Leaflet adds
          // the marker's own screen y on top of this, so within the tier the
          // southern dot paints in front — stable across renders without any
          // sort of our own.
          zIndexOffset: Z_POST,
        });
        if (onPostPressRef.current) {
          marker.on('click', () => onPostPressRef.current?.(post));
        }
        // Hover preview (web/desktop only). Anchor the card to the marker's
        // current container pixel so it tracks the dot, not the cursor.
        if (onPostHoverRef.current) {
          marker.on('mouseover', () => {
            const pt = mapNow.latLngToContainerPoint(marker.getLatLng());
            onPostHoverRef.current?.(post, { x: pt.x, y: pt.y });
          });
          marker.on('mouseout', () => onPostHoverEndRef.current?.());
        }
        marker.addTo(mapNow);
        setPostMarkerDocked(marker, postId === dockedPostIdRef.current);
        postMarkersRef.current.set(key, marker);
        postMarkerIconKeyRef.current.set(key, iconKey);
      }

      for (const [key, marker] of postMarkersRef.current) {
        if (!nextKeys.has(key)) {
          marker.off();
          mapNow.removeLayer(marker);
          postMarkersRef.current.delete(key);
          postMarkerIconKeyRef.current.delete(key);
        }
      }
    };

    renderPostsRef.current = renderPosts;
    renderPosts();
    // All inputs are read through refs at call time, so this wiring runs once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded]);

  // Docked post changed: move the ring from the old dot to the new one. A
  // post inside a cluster has no dot of its own, so it's skipped; the ring
  // appears when zooming in builds the dot (renderPosts checks the ref).
  useEffect(() => {
    if (!isLoaded) return;
    const prev = prevDockedPostIdRef.current;
    if (prev && prev !== dockedPostId) {
      setPostMarkerDocked(postMarkersRef.current.get(`o:${prev}`), false);
    }
    if (dockedPostId) setPostMarkerDocked(postMarkersRef.current.get(`o:${dockedPostId}`), true);
    prevDockedPostIdRef.current = dockedPostId;
    // postMarkersRef is a stable ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dockedPostId, isLoaded]);

  // Rebuild the observation index when the post set changes, then repaint.
  //
  // The radius is deliberately tight. Supercluster measures it against its
  // own 512-unit extent while Leaflet draws 256px tiles, so the effective
  // screen distance is about half the number: 32 here is ~16px between
  // centers, roughly the radius of a dot's 32px footprint (22 + border +
  // halo). That merges dots that actually overlap and leaves ones sitting
  // side by side alone — the point is to fix pileups, not to hide a pair that
  // reads perfectly well as two. A wider radius (44 was tried) swallows
  // legible neighbors and drains the category color out of the whole map.
  //
  // minPoints 2 because two observations on the same spot is exactly the case
  // worth merging: one drawn invisibly behind the other is the bug here.
  useEffect(() => {
    if (!isLoaded) return;
    const withLocation = posts.filter((p) => p.location);
    const byId = new Map<string, Post>();
    const features: PointFeature<PostPointProps>[] = [];
    for (const post of withLocation) {
      byId.set(post.id, post);
      const category = normalizePostCategory(post.category);
      features.push({
        type: 'Feature',
        properties: {
          postId: post.id,
          progress: category === 'progress' ? 1 : 0,
          risk: category === 'risk' ? 1 : 0,
          news: category === 'news' ? 1 : 0,
        },
        geometry: {
          type: 'Point',
          coordinates: [post.location!.longitude, post.location!.latitude],
        },
      });
    }
    postsByIdRef.current = byId;
    if (!postClusterRef.current) {
      postClusterRef.current = new Supercluster<PostPointProps, CategoryTally>({
        radius: 32,
        maxZoom: MAX_ZOOM,
        minPoints: 2,
        map: (props) => ({ progress: props.progress, risk: props.risk, news: props.news }),
        reduce: (acc, props) => {
          acc.progress += props.progress;
          acc.risk += props.risk;
          acc.news += props.news;
        },
      });
    }
    postClusterRef.current.load(features);
    renderPostsRef.current();
  }, [posts, isLoaded]);

  // Repaint the observations when the theme flips — the fills are read from
  // colorsRef at build time, so the existing icons hold the old palette.
  useEffect(() => {
    if (!isLoaded) return;
    postMarkerIconKeyRef.current.clear();
    renderPostsRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colors, isLoaded]);

  // Repaint on the observations-layer toggle itself: renderPosts reads
  // visibleRef, but nothing else re-runs it on a plain toggle (no pan/zoom,
  // no post-set change), so without this the layer wouldn't clear/redraw
  // until the next unrelated render.
  useEffect(() => {
    if (!isLoaded) return;
    renderPostsRef.current();
  }, [visible, isLoaded]);
}
